// Apolo IDE — TouchButton (Sprint v9 M4).
//
// Reusable button que enforça touch target ≥44×44 mobile (Apple HIG) /
// densidade desktop. Variants padronizadas pra evitar repetição de Tailwind
// strings longas nos consumers.
//
// Variants:
//   primary    — bg-apolo-accent, text-apolo-bg (CTA azul cósmico)
//   secondary  — border-apolo-borderSubtle bg-surface2, text-secondary (default outline)
//   ghost      — sem borda, hover bg-surface2 (icon-only chip)
//   danger     — text-apolo-error em hover (destrutivo)
//
// Sizing:
//   icon       — quadrado h-11 w-11 / lg:h-9 lg:w-9 (icon-only)
//   default    — h-11 px-3 / lg:h-auto lg:py-1.5 (label CTA)
//
// Uso:
//   <TouchButton variant="primary" onClick={save}>Salvar</TouchButton>
//   <TouchButton variant="ghost" size="icon" aria-label="Fechar"><X/></TouchButton>

import clsx from 'clsx';
import { forwardRef } from 'react';

const VARIANTS = {
  primary:
    'bg-apolo-accent text-apolo-bg hover:bg-apolo-accentHover focus-visible:ring-apolo-accentRing disabled:opacity-40',
  secondary:
    'border border-apolo-borderSubtle bg-apolo-surface2 text-apolo-textSecondary hover:border-apolo-borderStrong hover:bg-apolo-surface3 hover:text-apolo-textPrimary focus-visible:ring-apolo-accentRing disabled:opacity-40',
  ghost:
    'text-apolo-textTertiary hover:bg-apolo-surface2 hover:text-apolo-textPrimary focus-visible:ring-apolo-accentRing',
  danger:
    'text-apolo-textTertiary hover:bg-apolo-error/10 hover:text-apolo-error focus-visible:ring-apolo-error/40',
};

const SIZES = {
  default: 'h-11 px-3 text-sm gap-1.5 lg:h-9 lg:py-1.5',
  icon: 'h-11 w-11 lg:h-9 lg:w-9',
  sm: 'h-9 px-2 text-xs gap-1 lg:h-7 lg:px-2',
};

// FASE-11 (tsc-codetab): o JSDoc precisa anotar o CALLBACK do forwardRef — no
// const de fora o checkJs não o aplica e os props degradam pra `{}`.
const TouchButton = forwardRef(
  /**
   * @param {{
   *   variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
   *   size?: 'default' | 'icon' | 'sm';
   *   className?: string;
   *   children?: import('react').ReactNode;
   * } & import('react').ButtonHTMLAttributes<HTMLButtonElement>} props
   * @param {import('react').Ref<HTMLButtonElement>} ref
   */
  function TouchButton(
    { variant = 'secondary', size = 'default', className = '', type = 'button', children, ...rest },
    ref,
  ) {
  return (
    <button
      ref={ref}
      type={type}
      className={clsx(
        'inline-flex shrink-0 items-center justify-center rounded-md font-medium transition-colors',
        'focus:outline-none focus-visible:ring-2',
        'disabled:cursor-not-allowed',
        SIZES[size] || SIZES.default,
        VARIANTS[variant] || VARIANTS.secondary,
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
  },
);

export default TouchButton;
